/**
 * Training and recertification, following the SC Criminal Justice Academy's Mandatory Retraining
 * Notification (MRN) form.
 *
 * A certification runs three years to its renewal date. Each of the three years needs that year's
 * required topics for the officer's certification class (Class 1: DV, Legal and EVO every year); the
 * whole period needs one mental-health awareness in-service and (for most classes) a total of in-service
 * hours. The agency adds its own yearly requirements on top — by default firearms qualification,
 * less-lethal, use-of-force policy review and ground defense (firearms and less-lethal only when the
 * officer is issued them). Training done late can be marked as the make-up for the year it was missed.
 */

export interface TrainingCategory {
  key: string;
  name: string;
  /** One of the MRN's own lines (can't be removed). */
  mrn: boolean;
}

/** The MRN's categories plus the common agency ones. Agencies can add more. */
export const TRAINING_CORE: TrainingCategory[] = [
  { key: 'firearms', name: 'Firearms qualification', mrn: true },
  { key: 'less_lethal', name: 'Less-lethal weapons', mrn: true },
  { key: 'use_of_force', name: 'Use of force policy review', mrn: true },
  { key: 'evo', name: 'EVO (emergency vehicle operation)', mrn: true },
  { key: 'dv', name: 'Domestic violence', mrn: true },
  { key: 'legal', name: 'Legal update', mrn: true },
  { key: 'mental_health', name: 'Mental health awareness', mrn: true },
  { key: 'ground_defense', name: 'Ground defense / defensive tactics', mrn: false },
  { key: 'in_service', name: 'Other in-service training', mrn: true },
];

/** What the state requires, by certification class (MRN Section II). */
export const CERT_CLASSES = {
  class1: { label: 'Class 1 LEO', annual: ['dv', 'legal', 'evo'], hours: 40 },
  class1_leco: { label: 'Class 1 LECO', annual: ['dv', 'legal', 'evo'], hours: 120 },
  class2: { label: 'Class 2', annual: [], hours: 120 },
  class3: { label: 'Class 3', annual: ['legal'], hours: null },
  class3_sleco: { label: 'Class 3 SLECO', annual: ['legal'], hours: 120 },
} as const satisfies Record<string, { label: string; annual: readonly string[]; hours: number | null }>;
export type CertClass = keyof typeof CERT_CLASSES;
export const CERT_CLASS_KEYS = Object.keys(CERT_CLASSES) as CertClass[];
/** The agency's own yearly requirements to start with (the training officer can change them). */
export const DEFAULT_AGENCY_ANNUAL = ['firearms', 'less_lethal', 'use_of_force', 'ground_defense'];
/** Class 2 officers usually aren't issued a firearm. */
export const firearmsByDefault = (c: CertClass) => c !== 'class2';

export interface TrainingRecordLite {
  id: string;
  category: string;
  completed_on: string; // YYYY-MM-DD
  hours: number;
  title: string;
  /** A date inside the cycle year this training makes up for (done late). */
  makeup_for?: string | null;
}

export interface TrainingProfileLite {
  certClass: CertClass | null;
  renewalDate: string | null; // YYYY-MM-DD
  firearmsIssued: boolean;
  lessLethalIssued: boolean;
}

export type ItemStatus = 'done' | 'made_up' | 'due' | 'missed' | 'upcoming';

const addYears = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  // Feb 29 → Feb 28 in a non-leap year.
  const t = new Date(Date.UTC(y + n, m - 1, d));
  if (t.getUTCMonth() !== m - 1) t.setUTCDate(0);
  return t.toISOString().slice(0, 10);
};
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/** The topics this officer has to do every year. */
export function annualRequirements(p: TrainingProfileLite, agencyAnnual: string[] = []): string[] {
  if (!p.certClass) return [];
  const all: string[] = [...CERT_CLASSES[p.certClass].annual, ...agencyAnnual];
  // Firearms and less-lethal only when issued (the MRN's "NA if not issued").
  return [...new Set(all)].filter((k) => (k === 'firearms' ? p.firearmsIssued : k === 'less_lethal' ? p.lessLethalIssued : true));
}

export function trainingProgress(p: TrainingProfileLite, records: TrainingRecordLite[], today: string, agencyAnnual: string[] = []) {
  if (!p.certClass || !p.renewalDate) return { status: 'not_set' as const, percent: 0 };
  const cls = CERT_CLASSES[p.certClass];
  const end = p.renewalDate;
  const start = addYears(end, -3);
  const need = annualRequirements(p, agencyAnnual);
  const inPeriod = records.filter((r) => r.completed_on >= start && r.completed_on < end);
  const years = [1, 2, 3].map((n) => {
    const ys = addYears(start, n - 1);
    const ye = addYears(start, n);
    const when: 'past' | 'current' | 'future' = today >= ye ? 'past' : today >= ys ? 'current' : 'future';
    const inYear = (d: string | null | undefined) => !!d && d >= ys && d < ye;
    const items = need.map((key) => {
      const mine = records.filter((r) => r.category === key);
      const done = mine.filter((r) => !r.makeup_for && inYear(r.completed_on) && r.completed_on >= start && r.completed_on < end).sort((a, b) => b.completed_on.localeCompare(a.completed_on))[0] ?? null;
      const makeup = done ? null : mine.find((r) => inYear(r.makeup_for)) ?? null;
      const status: ItemStatus = done ? 'done' : makeup ? 'made_up' : when === 'past' ? 'missed' : when === 'current' ? 'due' : 'upcoming';
      return { key, status, record: done ?? makeup };
    });
    const hours = inPeriod.filter((r) => r.completed_on >= ys && r.completed_on < ye).reduce((s, r) => s + Number(r.hours || 0), 0);
    return { n, start: ys, end: ye, when, items, hours };
  });
  const mh = inPeriod.filter((r) => r.category === 'mental_health').sort((a, b) => b.completed_on.localeCompare(a.completed_on))[0] ?? null;
  const hoursDone = Math.round(inPeriod.reduce((s, r) => s + Number(r.hours || 0), 0) * 10) / 10;
  const hoursNeed = cls.hours;
  const all = years.flatMap((y) => y.items);
  const isDone = (i: { status: ItemStatus }) => i.status === 'done' || i.status === 'made_up';
  const doneItems = all.filter(isDone).length;
  const parts = all.length + 1 + (hoursNeed ? 1 : 0);
  const got = doneItems + (mh ? 1 : 0) + (hoursNeed ? Math.min(1, hoursDone / hoursNeed) : 0);
  const percent = Math.round((got / parts) * 100);
  const missed = all.filter((i) => i.status === 'missed').map((i) => i.key);
  const dueNow = years.find((y) => y.when === 'current')?.items.filter((i) => i.status === 'due').map((i) => i.key) ?? [];
  const daysLeft = daysBetween(today, end);
  const complete = all.every(isDone) && !!mh && (!hoursNeed || hoursDone >= hoursNeed);
  const status = complete
    ? ('complete' as const)
    : daysLeft < 0
      ? ('expired' as const)
      : missed.length
        ? ('behind' as const)
        : daysLeft <= 90
          ? ('due_soon' as const)
          : ('on_track' as const);
  return {
    status,
    percent,
    certClass: p.certClass,
    classLabel: cls.label,
    period: { start, end },
    daysLeft,
    years,
    mentalHealth: mh,
    hours: { done: hoursDone, need: hoursNeed },
    missed,
    dueNow,
  };
}
export type TrainingProgress = ReturnType<typeof trainingProgress>;

export const TRAINING_STATUS_LABEL: Record<string, string> = {
  complete: 'Ready to renew',
  on_track: 'On track',
  due_soon: 'Renewal coming up',
  behind: 'Behind — make-up needed',
  expired: 'Renewal date passed',
  not_set: 'Certification not set up',
};

// ─── Firearms qualification scores ─────────────────────────────────────────
export type QualLight = 'day' | 'night' | 'other';
export const QUAL_COURSES = ['Day qualification', 'Low-light / night qualification', 'Remedial', 'Transition / new weapon'] as const;
/** Day or night course, from the course name (remedial / transition count only toward overall). */
export function qualLight(course: unknown): QualLight {
  const c = String(course ?? '').toLowerCase();
  if (/night|low[\s-]?light|dim/.test(c)) return 'night';
  if (!c || /\bday\b|daylight/.test(c)) return 'day';
  return 'other';
}
export interface QualLite {
  id?: string;
  completed_on: string;
  details?: { course?: unknown; score?: unknown; passed?: unknown } | null;
}
export interface QualDecline {
  light: 'day' | 'night';
  /** Oldest → newest: each score lower than the one before. */
  scores: number[];
  dates: string[];
}
const avg = (xs: number[]) => (xs.length ? Math.round((xs.reduce((s, x) => s + x, 0) / xs.length) * 10) / 10 : null);
/**
 * An officer's average qualification score — day, night and overall — and a warning when the score
 * has dropped two qualifications in a row (compared course to course: day with day, night with night).
 */
export function qualStats(quals: QualLite[]) {
  const scored = quals
    .map((q) => ({ date: q.completed_on.slice(0, 10), light: qualLight(q.details?.course), score: q.details?.score == null || q.details?.score === '' ? NaN : Number(q.details.score) }))
    .filter((q) => Number.isFinite(q.score))
    .sort((a, b) => a.date.localeCompare(b.date));
  const of = (l: QualLight) => scored.filter((q) => q.light === l);
  const declines: QualDecline[] = [];
  for (const light of ['day', 'night'] as const) {
    const last3 = of(light).slice(-3);
    if (last3.length === 3 && last3[0]!.score > last3[1]!.score && last3[1]!.score > last3[2]!.score) declines.push({ light, scores: last3.map((q) => q.score), dates: last3.map((q) => q.date) });
  }
  return {
    day: { avg: avg(of('day').map((q) => q.score)), count: of('day').length },
    night: { avg: avg(of('night').map((q) => q.score)), count: of('night').length },
    overall: { avg: avg(scored.map((q) => q.score)), count: scored.length },
    declines,
  };
}
export type QualStats = ReturnType<typeof qualStats>;
